import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { UserSession } from '@/api/entities';
import { normalizeTenantId } from '@/lib/tenantUtils';

/** Stop growing duration after this much idle time (no interaction / navigation). */
const IDLE_CAP_MS = 15 * 60 * 1000;
/** Resume an existing tab session if last activity was within this window. */
const SESSION_RESUME_MS = 30 * 60 * 1000;
const SAVE_INTERVAL = 60 * 1000;

function getSessionStorageKey(userId, tenantId) {
  return `neo-user-session:${tenantId}:${userId}`;
}

function getSessionTrackApiDisabledKey(userId, tenantId) {
  return `neo-user-session-api-disabled:${tenantId}:${userId}`;
}

function isSessionTrackApiDisabled(userId, tenantId) {
  try {
    return sessionStorage.getItem(getSessionTrackApiDisabledKey(userId, tenantId)) === '1';
  } catch {
    return false;
  }
}

function setSessionTrackApiDisabled(userId, tenantId) {
  try {
    sessionStorage.setItem(getSessionTrackApiDisabledKey(userId, tenantId), '1');
  } catch {
    // ignore
  }
}

function clearSessionTrackApiDisabled(userId, tenantId) {
  try {
    sessionStorage.removeItem(getSessionTrackApiDisabledKey(userId, tenantId));
  } catch {
    // ignore
  }
}

function clearLocalSessionState(userId, tenantId) {
  try {
    sessionStorage.removeItem(getSessionStorageKey(userId, tenantId));
  } catch {
    // ignore
  }
}

function isSessionTrackBadRequest(error) {
  const status = error?.status;
  return status === 400 || String(error?.message || '').includes('(400)');
}

function getNowIso() {
  return new Date().toISOString();
}

function calculateDurationMinutes(startedAt, lastActiveAt) {
  const start = new Date(startedAt).getTime();
  const end = new Date(lastActiveAt).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return 0;
  return Math.max(0, Math.round((end - start) / 60000));
}

function getApiBase() {
  if (typeof window === 'undefined') return 'https://api.neogtm.com';
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'https://api.neogtm.com';
  return 'https://api.neogtm.com';
}

/**
 * Tracks user sessions for analytics.
 * Sends user_email, page_views, duration_minutes, and session_end (on leave).
 */
export default function SessionTracker({ user, tenantId }) {
  const location = useLocation();
  const sessionIdRef = useRef(null);
  const sessionStartRef = useRef(null);
  const lastActivityRef = useRef(null);
  const pageViewsRef = useRef(1);
  const lastPathRef = useRef(null);
  const createdRef = useRef(false);
  const flushInFlightRef = useRef(false);
  const trackingDisabledRef = useRef(false);
  const endedRef = useRef(false);

  // Heartbeat + create/update lifecycle (user/tenant scoped).
  useEffect(() => {
    if (!user?.id || !tenantId) return undefined;

    // Layout boots with activeTenant='instructure' before session resolves (e.g. smowl users).
    // Creating UserSession with the wrong tenant_id → 400. Wait until tenant matches the user.
    if (user.role !== 'admin') {
      const userTenant = normalizeTenantId(user.tenant_id);
      const activeTenant = normalizeTenantId(tenantId);
      if (userTenant && activeTenant && userTenant !== activeTenant) {
        return undefined;
      }
    }

    clearSessionTrackApiDisabled(user.id, tenantId);
    trackingDisabledRef.current = isSessionTrackApiDisabled(user.id, tenantId);
    endedRef.current = false;

    const storageKey = getSessionStorageKey(user.id, tenantId);
    const now = Date.now();

    let existing = null;
    try {
      const raw = sessionStorage.getItem(storageKey);
      existing = raw ? JSON.parse(raw) : null;
    } catch {
      existing = null;
    }

    const shouldResumeExisting =
      existing?.id &&
      existing?.session_start &&
      existing?.last_activity &&
      now - new Date(existing.last_activity).getTime() < SESSION_RESUME_MS;

    sessionIdRef.current = shouldResumeExisting ? existing.id : null;
    sessionStartRef.current = shouldResumeExisting ? existing.session_start : getNowIso();
    lastActivityRef.current = shouldResumeExisting ? existing.last_activity : getNowIso();
    pageViewsRef.current = shouldResumeExisting
      ? Math.max(1, Number(existing.page_views) || 1)
      : 1;
    createdRef.current = Boolean(shouldResumeExisting);
    lastPathRef.current = `${location.pathname}${location.search}`;

    const persistLocalSessionState = () => {
      if (!sessionIdRef.current || !sessionStartRef.current || !lastActivityRef.current) return;
      try {
        sessionStorage.setItem(
          storageKey,
          JSON.stringify({
            id: sessionIdRef.current,
            session_start: sessionStartRef.current,
            last_activity: lastActivityRef.current,
            page_views: pageViewsRef.current,
          }),
        );
      } catch {
        // Ignore storage write failures.
      }
    };

    const buildPayload = ({ markEnded = false } = {}) => {
      const nowIso = getNowIso();
      const lastActivityMs = new Date(lastActivityRef.current).getTime();
      const idleMs = Number.isFinite(lastActivityMs) ? Date.now() - lastActivityMs : 0;

      // Cap duration at last real activity once idle past threshold (don't balloon open tabs).
      let effectiveLastActivity = lastActivityRef.current;
      if (markEnded) {
        if (idleMs <= IDLE_CAP_MS) {
          effectiveLastActivity = nowIso;
          lastActivityRef.current = nowIso;
        }
      } else if (idleMs > IDLE_CAP_MS) {
        // Heartbeat while idle: keep duration frozen at last activity; do not extend.
        effectiveLastActivity = lastActivityRef.current;
      }

      const payload = {
        user_id: user.id,
        tenant_id: tenantId,
        user_email: user.email ?? null,
        session_start: sessionStartRef.current,
        duration_minutes: calculateDurationMinutes(sessionStartRef.current, effectiveLastActivity),
        page_views: Math.max(0, Math.floor(Number(pageViewsRef.current) || 0)),
      };
      if (markEnded) {
        payload.session_end = effectiveLastActivity;
      }
      return payload;
    };

    const disableTracking = () => {
      setSessionTrackApiDisabled(user.id, tenantId);
      trackingDisabledRef.current = true;
      clearLocalSessionState(user.id, tenantId);
      sessionIdRef.current = null;
      createdRef.current = false;
    };

    const patchWithKeepalive = (sessionId, payload) => {
      try {
        void fetch(`${getApiBase()}/api/entities/UserSession/${encodeURIComponent(sessionId)}`, {
          method: 'PATCH',
          credentials: 'include',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            ...(tenantId ? { 'x-tenant-id': tenantId } : {}),
            ...(user.id ? { 'x-user-id': String(user.id) } : {}),
          },
          body: JSON.stringify(payload),
        });
        return true;
      } catch {
        return false;
      }
    };

    const flushSession = async ({ markEnded = false, useKeepalive = false } = {}) => {
      if (!user?.id || !tenantId || flushInFlightRef.current) return;
      if (endedRef.current && !markEnded) return;
      if (isSessionTrackApiDisabled(user.id, tenantId)) {
        trackingDisabledRef.current = true;
      }
      if (trackingDisabledRef.current) return;
      if (!sessionStartRef.current || !lastActivityRef.current) return;

      // Skip idle heartbeats that wouldn't change anything useful (still allow end).
      if (!markEnded) {
        const lastActivityMs = new Date(lastActivityRef.current).getTime();
        if (Number.isFinite(lastActivityMs) && Date.now() - lastActivityMs > IDLE_CAP_MS) {
          return;
        }
      }

      flushInFlightRef.current = true;

      try {
        const payload = buildPayload({ markEnded });

        if (!createdRef.current) {
          try {
            const created = await UserSession.create(payload);
            sessionIdRef.current = created?.id || created?._id || null;
            createdRef.current = true;
            if (markEnded) {
              endedRef.current = true;
              clearLocalSessionState(user.id, tenantId);
            } else {
              persistLocalSessionState();
            }
            return;
          } catch (createErr) {
            if (isSessionTrackBadRequest(createErr)) {
              disableTracking();
              return;
            }
            throw createErr;
          }
        }

        if (!sessionIdRef.current) return;

        if (useKeepalive) {
          patchWithKeepalive(sessionIdRef.current, payload);
          if (markEnded) {
            endedRef.current = true;
            clearLocalSessionState(user.id, tenantId);
          } else {
            persistLocalSessionState();
          }
          return;
        }

        try {
          await UserSession.update(sessionIdRef.current, payload);
        } catch (updateErr) {
          if (isSessionTrackBadRequest(updateErr)) {
            disableTracking();
            return;
          }
          throw updateErr;
        }

        if (markEnded) {
          endedRef.current = true;
          clearLocalSessionState(user.id, tenantId);
        } else {
          persistLocalSessionState();
        }
      } catch (error) {
        if (!isSessionTrackBadRequest(error)) {
          console.warn('SessionTracker: failed to persist session', error);
        }
      } finally {
        flushInFlightRef.current = false;
      }
    };

    const markActivity = () => {
      if (endedRef.current) return;
      lastActivityRef.current = getNowIso();
      persistLocalSessionState();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Previous hide closed the session — start a fresh one on return.
        if (endedRef.current) {
          endedRef.current = false;
          sessionIdRef.current = null;
          createdRef.current = false;
          sessionStartRef.current = getNowIso();
          lastActivityRef.current = sessionStartRef.current;
          pageViewsRef.current = Math.max(1, pageViewsRef.current);
          clearLocalSessionState(user.id, tenantId);
        } else {
          markActivity();
        }
        void flushSession();
      } else {
        // Best-effort close so duration stops growing while the tab is hidden.
        void flushSession({ markEnded: true, useKeepalive: true });
      }
    };

    const handlePageHide = () => {
      void flushSession({ markEnded: true, useKeepalive: true });
    };

    const handleBeforeUnload = () => {
      void flushSession({ markEnded: true, useKeepalive: true });
    };

    const activityEvents = ['click', 'keydown', 'mousemove', 'scroll', 'focus', 'touchstart'];
    const throttledActivityHandler = (() => {
      let lastRun = 0;
      return () => {
        const current = Date.now();
        if (current - lastRun < 15000) return;
        lastRun = current;
        markActivity();
      };
    })();

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, throttledActivityHandler, { passive: true });
    });
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handleBeforeUnload);

    void flushSession();
    const intervalId = window.setInterval(() => {
      void flushSession();
    }, SAVE_INTERVAL);

    return () => {
      window.clearInterval(intervalId);
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, throttledActivityHandler);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      void flushSession({ markEnded: true });
    };
    // location is tracked in a separate effect for page_views
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: restart only on user/tenant
  }, [tenantId, user?.id, user?.email, user?.role, user?.tenant_id]);

  // Increment page_views on client-side route changes (monotonic within the session).
  useEffect(() => {
    if (!user?.id || !tenantId) return;

    const pathKey = `${location.pathname}${location.search}`;
    if (lastPathRef.current == null) {
      lastPathRef.current = pathKey;
      pageViewsRef.current = Math.max(1, pageViewsRef.current);
      return;
    }
    if (lastPathRef.current === pathKey) return;

    lastPathRef.current = pathKey;
    pageViewsRef.current = Math.max(1, pageViewsRef.current) + 1;
    lastActivityRef.current = getNowIso();

    try {
      const storageKey = getSessionStorageKey(user.id, tenantId);
      const raw = sessionStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        sessionStorage.setItem(
          storageKey,
          JSON.stringify({
            ...parsed,
            last_activity: lastActivityRef.current,
            page_views: pageViewsRef.current,
          }),
        );
      }
    } catch {
      // ignore
    }
  }, [location.pathname, location.search, tenantId, user?.id]);

  return null;
}
